import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { BadgeCheck, Mail, Phone, Ticket as TicketIcon, ScanLine, PlusCircle, Download } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Pill, Progress, Loader } from "@/components/Bits";
import { useAuth } from "@/context/AuthContext";

const field = "w-full h-11 bg-white/[0.04] border border-white/10 px-4 text-sm outline-none focus:border-neon/60 transition-colors";
const TABS = ["Overview", "Tickets", "Petitions", "Writing", "Spotlight", "Organizer", "Verify"];

const Dashboard = () => {
  const { user, loading, login, refresh } = useAuth();
  const [tab, setTab] = useState("Overview");
  const [tickets, setTickets] = useState<any[]>([]);
  const [mine, setMine] = useState<any[]>([]);
  const [signed, setSigned] = useState<any[]>([]);
  const [myEvents, setMyEvents] = useState<any[]>([]);
  const [org, setOrg] = useState<any>({ status: "none" });
  const [myBlogs, setMyBlogs] = useState<any[]>([]);
  const [mySpots, setMySpots] = useState<any[]>([]);
  const [received, setReceived] = useState<any[]>([]);
  const [sent, setSent] = useState<any[]>([]);
  const [orgForm, setOrgForm] = useState({ org_name: "", org_type: "collective", website: "", about: "" });
  const [otp, setOtp] = useState({ email: "", phone: "" });
  const [phone, setPhone] = useState("");
  const [scan, setScan] = useState("");

  useEffect(() => {
    if (!user) return;
    api.get("/tickets/mine").then((r) => setTickets(r.data)).catch(() => {});
    api.get("/petitions/mine").then((r) => setMine(r.data)).catch(() => {});
    api.get("/petitions/signed").then((r) => setSigned(r.data)).catch(() => {});
    api.get("/events/mine").then((r) => setMyEvents(r.data)).catch(() => {});
    api.get("/organizers/me").then((r) => setOrg(r.data)).catch(() => {});
    api.get("/blogs/mine").then((r) => setMyBlogs(r.data)).catch(() => {});
    api.get("/spotlight/mine").then((r) => setMySpots(r.data)).catch(() => {});
    api.get("/connects/received").then((r) => setReceived(r.data)).catch(() => {});
    api.get("/connects/sent").then((r) => setSent(r.data)).catch(() => {});
  }, [user]);

  if (loading) return <Loader />;
  if (!user)
    return (
      <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-32" data-testid="dashboard-auth-gate">
        <h1 className="font-bebas text-5xl tracking-wide uppercase">Sign in to see your circle</h1>
        <button onClick={login} data-testid="dashboard-login-btn" className="mt-8 bg-neon px-8 h-12 text-[12px] font-bold uppercase tracking-[0.2em] hover:pc-glow transition-shadow">Join the circle</button>
      </div>
    );

  const sendOtp = async (channel: string) => {
    try {
      const { data } = await api.post("/verify/send", { channel, phone: channel === "phone" ? phone : undefined });
      toast.success(channel === "email" ? "Code sent to your email." : `Code: ${data.dev_code} (SMS provider pending)`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const confirmOtp = async (channel: string) => {
    try {
      await api.post("/verify/confirm", { channel, code: (otp as any)[channel] });
      toast.success(`${channel} verified`);
      await refresh();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const applyOrganizer = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.post("/organizers/apply", orgForm);
      toast.success("Application submitted for admin review.");
      const { data } = await api.get("/organizers/me");
      setOrg(data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const respondConnect = async (id: string, decision: string) => {
    try {
      await api.post(`/connects/${id}/respond`, { decision });
      toast.success(decision === "accept" ? "Accepted — contact emails shared." : "Declined.");
      const { data } = await api.get("/connects/received");
      setReceived(data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const downloadTicket = async (t: any) => {
    try {
      const { data } = await api.get(`/tickets/${t.ticket_id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ticket_${t.ticket_id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(errMsg(e, "Could not download ticket"));
    }
  };

  const doCheckin = async () => {
    try {
      const { data } = await api.post("/tickets/checkin", { qr_payload: scan });
      toast.success(data.already ? `${data.attendee} was already checked in` : `${data.attendee} checked in`);
      setScan("");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-16" data-testid="dashboard-page">
      <div className="flex flex-wrap items-center gap-5">
        {user.picture ? (
          <img src={user.picture} alt={user.name} className="w-16 h-16 rounded-full border border-white/15" />
        ) : (
          <div className="w-16 h-16 grid place-items-center bg-neon font-bebas text-3xl">{user.name[0]}</div>
        )}
        <div>
          <h1 className="font-bebas text-5xl tracking-wide uppercase leading-none" data-testid="dashboard-name">{user.name}</h1>
          <div className="mt-3 flex flex-wrap gap-2">
            {user.badges?.includes("verified") && <Pill tone="blue">Verified</Pill>}
            {user.is_organizer && <Pill tone="yellow">Organizer</Pill>}
            {user.badges?.includes("fellow") && <Pill tone="yellow">Fellow</Pill>}
            {user.role === "admin" && <Pill tone="neon">Admin</Pill>}
            {!user.email_verified && <Pill>Email unverified</Pill>}
          </div>
        </div>
        <div className="ml-auto flex gap-3">
          <Link to="/petitions/new" data-testid="dash-new-petition" className="border border-white/20 px-5 h-11 grid place-items-center text-[12px] font-bold uppercase tracking-[0.18em] hover:border-neon hover:text-neon transition-colors">New petition</Link>
          {(user.is_organizer || user.role === "admin") && (
            <Link to="/events/new" data-testid="dash-new-event" className="bg-neon px-5 h-11 flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.18em] hover:pc-glow transition-shadow"><PlusCircle size={15} /> New event</Link>
          )}
        </div>
      </div>

      <div className="mt-12 flex flex-wrap gap-2 border-b border-white/10">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`dash-tab-${t.toLowerCase()}`}
            className={`px-5 h-11 text-[11px] font-bold uppercase tracking-[0.2em] border-b-2 transition-colors duration-200 ${tab === t ? "border-neon text-neon" : "border-transparent text-white/45 hover:text-white"}`}>
            {t}
          </button>
        ))}
      </div>

      <div className="mt-10">
        {tab === "Overview" && (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4" data-testid="dash-overview">
            {[
              { label: "Tickets", value: tickets.length },
              { label: "Petitions drafted", value: mine.length },
              { label: "Petitions signed", value: signed.length },
              { label: "Events hosted", value: myEvents.length },
              { label: "Blogs submitted", value: myBlogs.length },
            ].map((s) => (
              <div key={s.label} className="border border-white/10 bg-ink-surface p-6">
                <div className="font-bebas text-5xl tracking-wide">{s.value}</div>
                <div className="mt-1 text-[11px] uppercase tracking-[0.2em] text-white/40">{s.label}</div>
              </div>
            ))}
          </div>
        )}

        {tab === "Tickets" && (
          <div className="grid gap-6 md:grid-cols-2" data-testid="dash-tickets">
            {tickets.map((t) => (
              <div key={t.ticket_id} className="border border-white/10 bg-ink-surface p-6 flex gap-6">
                <div className="bg-white p-3 h-fit"><QRCodeSVG value={t.qr_payload} size={92} /></div>
                <div>
                  <h3 className="font-outfit font-bold">{t.event_title}</h3>
                  <p className="mt-1 text-sm text-white/50">{t.tier_name} · {t.amount > 0 ? `₹${t.amount}` : "Free"}</p>
                  {t.checked_in && <p className="mt-2 text-xs text-pcyellow uppercase tracking-widest">Checked in</p>}
                  <button onClick={() => downloadTicket(t)} data-testid={`ticket-pdf-btn-${t.ticket_id}`}
                    className="mt-3 h-9 px-4 border border-white/20 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.16em] hover:border-neon hover:text-neon transition-colors">
                    <Download size={13} /> PDF ticket
                  </button>
                </div>
              </div>
            ))}
            {tickets.length === 0 && <p className="text-sm text-white/40 flex items-center gap-2"><TicketIcon size={15} /> No tickets yet.</p>}
          </div>
        )}

        {tab === "Petitions" && (
          <div className="grid gap-10" data-testid="dash-petitions">
            <div>
              <h3 className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/40">Drafted by you</h3>
              <div className="mt-5 grid gap-4">
                {mine.map((p) => (
                  <Link key={p.petition_id} to={`/petitions/${p.petition_id}`} className="border border-white/10 bg-ink-surface p-6 hover:border-neon/50 transition-colors">
                    <div className="flex items-center justify-between gap-4">
                      <span className="font-outfit font-semibold">{p.title}</span>
                      <Pill tone={p.status === "approved" ? "blue" : p.status === "rejected" ? "neon" : "yellow"}>{p.status}</Pill>
                    </div>
                    <div className="mt-4"><Progress value={p.signature_count} goal={p.goal} /></div>
                  </Link>
                ))}
                {mine.length === 0 && <p className="text-sm text-white/40">Nothing drafted yet.</p>}
              </div>
            </div>
            <div>
              <h3 className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/40">Signed by you</h3>
              <div className="mt-5 grid gap-3">
                {signed.map((p) => (
                  <Link key={p.petition_id} to={`/petitions/${p.petition_id}`} className="text-sm text-white/65 hover:text-neon transition-colors">→ {p.title}</Link>
                ))}
                {signed.length === 0 && <p className="text-sm text-white/40">No signatures yet.</p>}
              </div>
            </div>
          </div>
        )}

        {tab === "Writing" && (
          <div data-testid="dash-writing">
            <div className="flex items-center justify-between gap-4">
              <h3 className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/40">Your blog submissions</h3>
              <Link to="/blogs/new" data-testid="dash-new-blog" className="bg-neon px-5 h-10 grid place-items-center text-[12px] font-bold uppercase tracking-[0.18em] hover:pc-glow transition-shadow">Submit a piece</Link>
            </div>
            <div className="mt-6 grid gap-4">
              {myBlogs.map((b) => (
                <Link key={b.blog_id} to={`/blogs/${b.blog_id}`} className="border border-white/10 bg-ink-surface p-6 flex flex-wrap items-center justify-between gap-4 hover:border-neon/50 transition-colors">
                  <div>
                    <div className="font-outfit font-semibold">{b.title}</div>
                    <div className="mt-1 text-[11px] uppercase tracking-[0.18em] text-white/35">{b.category} · {b.read_minutes} min · {b.claps} claps</div>
                  </div>
                  <Pill tone={b.status === "approved" ? "blue" : b.status === "rejected" ? "neon" : "yellow"}>{b.status}</Pill>
                </Link>
              ))}
              {myBlogs.length === 0 && <p className="text-sm text-white/40">Nothing submitted yet.</p>}
            </div>
          </div>
        )}

        {tab === "Spotlight" && (
          <div className="grid gap-10" data-testid="dash-spotlight">
            <div>
              <div className="flex items-center justify-between gap-4">
                <h3 className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/40">Your listings</h3>
                <Link to="/spotlight" data-testid="dash-new-spotlight" className="bg-neon px-5 h-10 grid place-items-center text-[12px] font-bold uppercase tracking-[0.18em] hover:pc-glow transition-shadow">Get spotlighted</Link>
              </div>
              <div className="mt-5 grid gap-4">
                {mySpots.map((s) => (
                  <div key={s.spotlight_id} className="border border-white/10 bg-ink-surface p-6 flex flex-wrap items-center justify-between gap-4">
                    <div>
                      <div className="font-outfit font-semibold">{s.name}</div>
                      <div className="mt-1 text-[11px] uppercase tracking-[0.18em] text-white/35">{s.category.replace(/_/g, " ")} · {s.city} · {s.endorsements} endorsements</div>
                    </div>
                    <Pill tone={s.status === "approved" ? "blue" : s.status === "rejected" ? "neon" : "yellow"}>{s.status}</Pill>
                  </div>
                ))}
                {mySpots.length === 0 && <p className="text-sm text-white/40">No listings yet.</p>}
              </div>
            </div>
            <div>
              <h3 className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/40">Connect requests received</h3>
              <div className="mt-5 grid gap-4" data-testid="dash-connects-received">
                {received.map((c, i) => (
                  <div key={c.connect_id} className="border border-white/10 bg-ink-surface p-6" data-testid={`connect-received-${i}`}>
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="text-sm"><span className="font-semibold">{c.requester_name}</span> <span className="text-white/40">→ {c.spotlight_name}</span></div>
                      <Pill tone={c.status === "accepted" ? "blue" : c.status === "declined" ? "neon" : "yellow"}>{c.status}</Pill>
                    </div>
                    <p className="mt-3 text-sm text-white/55">{c.message}</p>
                    {c.status === "accepted" && <p className="mt-2 text-xs text-pcblue">Contact: {c.requester_email}</p>}
                    {c.status === "pending" && (
                      <div className="mt-4 flex gap-3">
                        <button onClick={() => respondConnect(c.connect_id, "accept")} data-testid={`connect-accept-${i}`} className="h-9 px-5 bg-neon text-[11px] font-bold uppercase tracking-[0.18em] hover:pc-glow transition-shadow">Accept</button>
                        <button onClick={() => respondConnect(c.connect_id, "decline")} data-testid={`connect-decline-${i}`} className="h-9 px-5 border border-white/20 text-[11px] font-bold uppercase tracking-[0.18em] hover:border-neon hover:text-neon transition-colors">Decline</button>
                      </div>
                    )}
                  </div>
                ))}
                {received.length === 0 && <p className="text-sm text-white/40">No requests yet.</p>}
              </div>
            </div>
            <div>
              <h3 className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/40">Requests you sent</h3>
              <div className="mt-5 grid gap-3" data-testid="dash-connects-sent">
                {sent.map((c) => (
                  <div key={c.connect_id} className="flex items-center justify-between gap-4 text-sm text-white/65">
                    <span>→ {c.spotlight_name}</span>
                    <Pill tone={c.status === "accepted" ? "blue" : c.status === "declined" ? "neon" : "yellow"}>{c.status}</Pill>
                  </div>
                ))}
                {sent.length === 0 && <p className="text-sm text-white/40">Nothing sent yet.</p>}
              </div>
            </div>
          </div>
        )}

        {tab === "Organizer" && (
          <div className="grid gap-10 lg:grid-cols-2" data-testid="dash-organizer">
            <div className="border border-white/10 bg-ink-surface p-7">
              <h3 className="font-bebas text-3xl tracking-wide uppercase">Organizer status</h3>
              <p className="mt-3 text-sm text-white/50">Current: <span className="text-white">{org.status}</span></p>
              {org.status === "none" || org.status === "rejected" ? (
                <form onSubmit={applyOrganizer} className="mt-6 grid gap-4">
                  <input required placeholder="Organization / collective name" value={orgForm.org_name} onChange={(e) => setOrgForm({ ...orgForm, org_name: e.target.value })} data-testid="org-name-input" className={field} />
                  <input placeholder="Website (optional)" value={orgForm.website} onChange={(e) => setOrgForm({ ...orgForm, website: e.target.value })} data-testid="org-website-input" className={field} />
                  <textarea required rows={4} placeholder="What kind of events will you host?" value={orgForm.about} onChange={(e) => setOrgForm({ ...orgForm, about: e.target.value })} data-testid="org-about-input" className="w-full bg-white/[0.04] border border-white/10 p-4 text-sm outline-none focus:border-neon/60 transition-colors" />
                  <button data-testid="org-apply-btn" className="h-11 bg-neon text-[12px] font-bold uppercase tracking-[0.2em] hover:pc-glow transition-shadow">Apply for approval</button>
                </form>
              ) : (
                <p className="mt-4 text-sm text-white/55">{org.status === "pending" ? "Admin is reviewing your application." : "You're approved. Create events any time."}</p>
              )}
            </div>
            <div className="grid gap-6">
              <div className="border border-white/10 bg-ink-surface p-7">
                <h3 className="flex items-center gap-2 font-bebas text-3xl tracking-wide uppercase"><ScanLine size={20} className="text-neon" /> Check-in</h3>
                <p className="mt-2 text-sm text-white/50">Paste an attendee&apos;s ticket code to check them in.</p>
                <input value={scan} onChange={(e) => setScan(e.target.value)} placeholder="PC|evt_...|user_...|q_..." data-testid="checkin-input" className={`mt-4 ${field}`} />
                <button onClick={doCheckin} data-testid="checkin-btn" className="mt-3 h-11 w-full border border-white/20 text-[12px] font-bold uppercase tracking-[0.18em] hover:border-neon hover:text-neon transition-colors">Check in</button>
              </div>
              <div className="border border-white/10 bg-ink-surface p-7">
                <h3 className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/40">Your events</h3>
                <div className="mt-4 grid gap-3">
                  {myEvents.map((e) => (
                    <Link key={e.event_id} to={`/events/${e.event_id}`} className="flex items-center justify-between gap-4 text-sm text-white/70 hover:text-neon transition-colors">
                      <span>{e.title}</span><Pill tone={e.status === "approved" ? "blue" : "yellow"}>{e.status}</Pill>
                    </Link>
                  ))}
                  {myEvents.length === 0 && <p className="text-sm text-white/40">No events yet.</p>}
                </div>
              </div>
            </div>
          </div>
        )}

        {tab === "Verify" && (
          <div className="grid gap-6 md:grid-cols-2 max-w-3xl" data-testid="dash-verify">
            <div className="border border-white/10 bg-ink-surface p-7">
              <h3 className="flex items-center gap-2 font-outfit font-semibold"><Mail size={16} className="text-neon" /> Email</h3>
              <p className="mt-2 text-sm text-white/50">{user.email}</p>
              {user.email_verified ? (
                <p className="mt-4 flex items-center gap-2 text-sm text-pcblue"><BadgeCheck size={16} /> Verified</p>
              ) : (
                <>
                  <button onClick={() => sendOtp("email")} data-testid="send-email-otp" className="mt-4 h-10 w-full border border-white/20 text-[12px] font-bold uppercase tracking-[0.18em] hover:border-neon hover:text-neon transition-colors">Send code</button>
                  <input value={otp.email} onChange={(e) => setOtp({ ...otp, email: e.target.value })} placeholder="6-digit code" data-testid="email-otp-input" className={`mt-3 ${field}`} />
                  <button onClick={() => confirmOtp("email")} data-testid="confirm-email-otp" className="mt-3 h-10 w-full bg-neon text-[12px] font-bold uppercase tracking-[0.18em] hover:pc-glow transition-shadow">Verify email</button>
                </>
              )}
            </div>
            <div className="border border-white/10 bg-ink-surface p-7">
              <h3 className="flex items-center gap-2 font-outfit font-semibold"><Phone size={16} className="text-neon" /> Phone</h3>
              {user.phone_verified ? (
                <p className="mt-4 flex items-center gap-2 text-sm text-pcblue"><BadgeCheck size={16} /> Verified · {user.phone}</p>
              ) : (
                <>
                  <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+91 98765 43210" data-testid="phone-input" className={`mt-4 ${field}`} />
                  <button onClick={() => sendOtp("phone")} data-testid="send-phone-otp" className="mt-3 h-10 w-full border border-white/20 text-[12px] font-bold uppercase tracking-[0.18em] hover:border-neon hover:text-neon transition-colors">Send code</button>
                  <input value={otp.phone} onChange={(e) => setOtp({ ...otp, phone: e.target.value })} placeholder="6-digit code" data-testid="phone-otp-input" className={`mt-3 ${field}`} />
                  <button onClick={() => confirmOtp("phone")} data-testid="confirm-phone-otp" className="mt-3 h-10 w-full bg-neon text-[12px] font-bold uppercase tracking-[0.18em] hover:pc-glow transition-shadow">Verify phone</button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
